/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sliders, Trophy, GraduationCap, Users, Sparkles, ShoppingBag } from "lucide-react";

const CONDITION_LABELS = {
  pokemon_caught_unique: "Pokémon capturés (uniques)",
  badges_total: "Badges cumulés",
  trainer_badges: "Badges par le meilleur dresseur",
  trainers_count: "Nb de dresseurs actifs",
  league_winners: "Maîtres de la Ligue",
  region_unlocked: "Région débloquée",
  kanto_dex_no_legendary: "Pokédex Kanto sans les 5 légendaires",
  johto_caught: "Pokémon Johto capturés (uniques)",
  always: "Toujours",
};

function fmtCond(c) {
  const label = CONDITION_LABELS[c.type] || c.type;
  return `${label} ≥ ${c.value}`;
}

export default function AdminTuningPage() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("grades");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/tuning");
        setData(data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  if (!data) {
    return <div className="font-mono text-xs text-[#7A7A7A]">Chargement…</div>;
  }

  return (
    <div data-testid="admin-tuning-page">
      <div className="mb-4">
        <div className="overline">Admin · Équilibrage</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">Tableau d&apos;équilibrage</h1>
        <p className="text-xs text-[#4A4A4A] mt-2 max-w-3xl">
          Vue en lecture seule des paramètres de progression. Utilise cette page pour comparer les paliers actuels
          et proposer des ajustements. Les modifications se font ensuite dans les fichiers <code>grades.py</code> et
          <code> pokemon_data.py</code>.
        </p>
      </div>

      <div className="flex gap-1 mb-3 flex-wrap" data-testid="tuning-tabs">
        {[
          { id: "grades", label: "Grades", icon: GraduationCap },
          { id: "slots",  label: "Slots dresseur", icon: Users },
          { id: "gyms",   label: "Arènes", icon: Trophy },
          { id: "personalities", label: "Personnalités", icon: Sparkles },
          { id: "shop", label: "Boutique", icon: ShoppingBag },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`tuning-tab-${t.id}`}
              className={`btn-ink text-xs ${tab === t.id ? "btn-leaf" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <Icon size={12}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "grades" && <GradesTable grades={data.grades}/>}
      {tab === "slots" && <SlotsTable slots={data.slot_rules}/>}
      {tab === "gyms" && <GymsTable gyms={data.gyms}/>}
      {tab === "personalities" && <PersonalitiesTable personalities={data.personalities}/>}
      {tab === "shop" && <ShopTable catalog={data.shop_catalog}/>}
    </div>
  );
}

function TableWrap({ children, cols }) {
  return (
    <div className="panel overflow-auto">
      <table className="w-full text-left border-collapse" style={{ minWidth: cols * 100 }}>
        {children}
      </table>
    </div>
  );
}

function GradesTable({ grades }) {
  return (
    <TableWrap cols={9}>
      <thead className="bg-[#EAE4D9]">
        <tr>
          <Th>Grade</Th>
          <Th>Titre</Th>
          <Th>PC</Th>
          <Th>−% shop</Th>
          <Th>+% XP</Th>
          <Th>Badges cumulés</Th>
          <Th>Capturés</Th>
          <Th>Dresseurs</Th>
          <Th>Ligues gagnées</Th>
          <Th>Dex complet ?</Th>
        </tr>
      </thead>
      <tbody className="font-mono text-[11px]">
        {grades.map((g) => (
          <tr key={g.id} className="border-t border-[#D8D2C4]" data-testid={`tuning-grade-${g.key}`}>
            <Td>{g.id}</Td>
            <Td>
              <span className="font-serif-display text-base">{g.title}</span>
              <div className="text-[10px] text-[#7A7A7A]">{g.key}</div>
            </Td>
            <Td>{g.pc_capacity}</Td>
            <Td>{Math.round(g.shop_discount * 100)} %</Td>
            <Td>{Math.round(g.trainer_xp_bonus * 100)} %</Td>
            <Td className={g.req_badges_total ? "font-bold" : ""}>{g.req_badges_total}</Td>
            <Td>{g.req_caught}</Td>
            <Td>{g.req_active_trainers}</Td>
            <Td>{g.req_league_wins}</Td>
            <Td>{g.req_pokedex_complete ? "Oui" : "—"}</Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

function SlotsTable({ slots }) {
  return (
    <TableWrap cols={3}>
      <thead className="bg-[#EAE4D9]">
        <tr>
          <Th>Slot</Th>
          <Th>Étiquette actuelle</Th>
          <Th>Conditions</Th>
        </tr>
      </thead>
      <tbody className="font-mono text-[11px]">
        {slots.map((s) => (
          <tr key={s.slot} className="border-t border-[#D8D2C4]" data-testid={`tuning-slot-${s.slot}`}>
            <Td className="font-bold">#{s.slot}</Td>
            <Td>{s.label}</Td>
            <Td>
              {s.conditions.length === 0
                ? <span className="text-[#7A7A7A]">Aucune (débloqué d&apos;emblée)</span>
                : (
                  <ul className="list-disc pl-4 space-y-0.5">
                    {s.conditions.map((c, i) => (
                      <li key={i}>{fmtCond(c)}</li>
                    ))}
                  </ul>
                )
              }
            </Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

function GymsTable({ gyms }) {
  return (
    <TableWrap cols={6}>
      <thead className="bg-[#EAE4D9]">
        <tr>
          <Th>Ordre</Th>
          <Th>Région</Th>
          <Th>Arène</Th>
          <Th>Champion</Th>
          <Th>Type</Th>
          <Th>Difficulté</Th>
          <Th>Niveau conseillé (×5)</Th>
        </tr>
      </thead>
      <tbody className="font-mono text-[11px]">
        {gyms.map((g) => (
          <tr key={g.id} className="border-t border-[#D8D2C4]" data-testid={`tuning-gym-${g.id}`}>
            <Td className="font-bold">#{g.order ?? "?"}</Td>
            <Td>{(g.region || "kanto").toUpperCase()}</Td>
            <Td className="font-serif-display text-base">{g.name}</Td>
            <Td>{g.leader}</Td>
            <Td>{g.type}</Td>
            <Td>{g.difficulty}</Td>
            <Td>Lv {g.difficulty * 5}</Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

function PersonalitiesTable({ personalities }) {
  return (
    <TableWrap cols={4}>
      <thead className="bg-[#EAE4D9]">
        <tr>
          <Th>Personnalité</Th>
          <Th>Description</Th>
          <Th>Poids d&apos;actions</Th>
          <Th>Modificateurs</Th>
        </tr>
      </thead>
      <tbody className="font-mono text-[11px]">
        {Object.entries(personalities).map(([key, p]) => (
          <tr key={key} className="border-t border-[#D8D2C4] align-top" data-testid={`tuning-perso-${key}`}>
            <Td className="font-serif-display text-base">{p.label}</Td>
            <Td className="text-[10px] max-w-[280px]">{p.description}</Td>
            <Td>
              <ul className="list-disc pl-4 space-y-0.5">
                {Object.entries(p.weights || {}).map(([k, v]) => (
                  <li key={k}>{k} : {v}</li>
                ))}
              </ul>
            </Td>
            <Td>
              <ul className="list-disc pl-4 space-y-0.5">
                {Object.entries(p.modifiers || {}).map(([k, v]) => (
                  <li key={k}>{k} : {v}</li>
                ))}
              </ul>
            </Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

function ShopTable({ catalog }) {
  const rows = Object.entries(catalog).sort(
    (a, b) => (a[1].unlock_trainers || 0) - (b[1].unlock_trainers || 0),
  );
  return (
    <TableWrap cols={3}>
      <thead className="bg-[#EAE4D9]">
        <tr>
          <Th>Article</Th>
          <Th>Prix (₽)</Th>
          <Th>Débloqué à N dresseurs recrutés</Th>
        </tr>
      </thead>
      <tbody className="font-mono text-[11px]">
        {rows.map(([id, meta]) => (
          <tr key={id} className="border-t border-[#D8D2C4]" data-testid={`tuning-shop-${id}`}>
            <Td>{id}</Td>
            <Td>{meta.price}</Td>
            <Td>{meta.unlock_trainers}</Td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

function Th({ children }) {
  return <th className="p-2 overline text-[10px] whitespace-nowrap">{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`p-2 align-top ${className}`}>{children}</td>;
}
